<template>
  <div>
    <BackButton v-if="isGoBackStatePossible" @goBack="goBackState" />
    <div v-if="state == 'unsubscribe'" class="max-w-lg w-full mx-auto px-4 py-12 text-center">
      <h2 class="text-2xl font-bold text-gray-900">You're unsubscribed</h2>
      <p class="mt-3 text-gray-700">
        You won't receive further photo alert emails for this ceremony.
      </p>
      <p v-if="unsubscribeStatus === 'error'" class="mt-3 text-sm text-red-600">
        We couldn't complete your request. Please try again later.
      </p>
      <p v-else-if="unsubscribeStatus === 'loading'" class="mt-3 text-sm text-gray-500">
        Processing your request…
      </p>
    </div>
    <SnapshotGallery
      name="Matthew Hoare"
      :images="galleryImages"
      @submit="gallerySelectionSubmitted"
      @notMe="notMeClick"
      v-if="state == 'gallery'"
    />
    <SnapshotCeremonySelect
      v-if="state == 'ceremony'"
      :events="events"
      :selected="personalDetails.event"
      :getRememberedDetails="getRememberedDetails"
      :remember-enabled="snapshotConfig.rememberMeEnabled"
      @submit="submitCeremony"
      @use-remembered="useRememberedDetails"
    />
    <ConsentForm
      v-if="state == 'consent'"
      :mode="snapshotConfig.consentMode"
      :title="snapshotConfig.consentTitle"
      :body="snapshotConfig.consentBody"
      :checkboxText="snapshotConfig.consentCheckboxText"
      :privacyUrl="ucdPrivacyUrl"
      :contactEmail="snapshotConfig.consentEmail"
      @accepted="handleConsentAccepted"
    />
    <SearchByFace
      v-if="state == 'face'"
      :name="personalDetails.name"
      :searching="searchInProgress"
      :search-error="searchError"
      :search-no-results="searchNoResults"
      :save-selfie-enabled="snapshotConfig.saveSelfieEnabled"
      :auto-search="autoSearchMode"
      @search-face="searchFace"
      @skip="handleSkipSelfie"
    />
    <SnapshotContactForm
      v-if="state == 'details'"
      :name="personalDetails.name"
      :email="personalDetails.email"
      :found-count="foundCount"
      :no-results="noResultsFound"
      :no-results-submitted="noResultsSubmitted"
      :no-results-notify-opt-in="noResultsNotifyOptIn"
      :has-selfie="Boolean(latestSelfieDataUrl)"
      :remembered="hasRememberedDetails"
      :remember-enabled="snapshotConfig.rememberMeEnabled"
      :save-selfie-enabled="snapshotConfig.saveSelfieEnabled && Boolean(latestSelfieDataUrl)"
      :save-selfie-consent-org-name="saveSelfieConsentOrgName"
      :save-selfie-consent-policy-url="saveSelfieConsentPolicyUrl"
      :save-selfie-consent-contact-email="saveSelfieConsentContactEmail"
      @submit="submitContactDetails"
      @forget-remembered="forgetRememberedDetails"
      @retake-selfie="retakeSelfieFromDetails"
    />
    <SnapshotImageSelector
      v-if="state == 'selector'"
      ref="imageSelector"
      :name="personalDetails.name"
      :images="images"
      :basketUrl="shareBasketdata"
      :is-free-digital="isFreeDigital"
      :free-download-success="freeDownloadSuccess"
      :free-download-error="freeDownloadError"
      :free-download-in-progress="freeDownloadInProgress"
      :last-order-nano-id="lastOrderNanoId"
      @checkout="submitImageSelection"
      @share="shareBasket"
      @retry-free-download="retryFreeDownload"
    />
    <!-- Product types disabled (digital-only) -->
    <!--
+    <ProductType 
+      v-if="state == 'type'"
+      @submit="submitProductType"
+    />
+    -->
    <CeremonyNotReady v-if="state == 'error'" />
  </div>
</template>
  
  <script>
import { trackEvent } from "../../Analytics.vue";
import SnapshotImageSelector from "./SnapshotImageSelector.vue";
// Import stylesheet
import axios from "axios";
import "vue-loading-overlay/dist/vue-loading.css";
import SnapshotGallery from "./SnapshotGallery.vue";
import CeremonyNotReady from "./CeremonyNotReady.vue";
import SearchByFace from "./SearchByFace.vue";
import BackButton from "./BackButton.vue";
import ConsentForm from "./ConsentForm.vue";
import SnapshotCeremonySelect from "./SnapshotCeremonySelect.vue";
import SnapshotContactForm from "./SnapshotContactForm.vue";
import { getSnapshotConfig, setRuntimeSnapshotConfig } from "../../snapshotConfig";
//import ProductType from "./ProductType.vue";
export default {
  name: "Snapshot",
  props: {
    organiser: Object,
    additionalFieldsInput: Array,
  },
  components: {
    SnapshotImageSelector,
    SnapshotGallery,
    CeremonyNotReady,
    SearchByFace,
    BackButton,
    ConsentForm,
    SnapshotCeremonySelect,
    SnapshotContactForm,
    //ProductType
  },
  data: function () {
    return {
      state: "ceremony",
      personalDetails: {},
      allImages: [],
      isLoading: false,
      searchInProgress: false,
      searchError: null,
      searchNoResults: false,
      events: [],
      registrationID: "",
      images: [],
      galleryImages: [],
      stepStack: [], // <— simple in-memory back stack
      showingPreviousNext: false,
      shareBasketdata: "",
      foundCount: 0,
      selfieSkipped: false,
      latestSelfieDataUrl: null,
      noResultsFound: false,
      noResultsSubmitted: false,
      noResultsNotifyOptIn: false,

      freeDownloadSuccess: false,
      freeDownloadError: null,
      freeDownloadInProgress: false,
      lastOrderNanoId: "",
      pendingState: null,
      pendingAutoSearch: null,
      unsubscribeStatus: null,
      autoSearchMode: false,
      orderFreeDigitalOverride: false,
    };
  },
  watch: {
    '$route.query.ceremony'() {
      this.loadSnapshotConfig();
      this.applyCeremonyIdFromQuery();
    },
  },
  mounted() {
    this.events = this.organiser["ceremonies"];
    // Seed the browser history with the initial step (no URL change)
    if (window?.history?.replaceState) {
      window.history.replaceState(
        { step: this.state },
        "",
        window.location.href
      );
    }
    window.addEventListener("popstate", this.onPopState);
    this.applyCeremonyIdFromQuery();
  },
  beforeDestroy() {
    window.removeEventListener("popstate", this.onPopState);
  },
  computed: {
    ucdPrivacyUrl() {
      return this.snapshotConfig.consentUrl || '';
    },
    requiresConsent() {
      return this.snapshotConfig.requiresConsent;
    },
    isFreeDigital() {
      // Example: depends on organiser config and order state
      if (this.snapshotConfig.isFreeDigital) {
        return true;
      }
      if (this.orderFreeDigitalOverride) {
        return true;
      }
      if (this.lastOrderNanoId && this.freeDownloadSuccess) {
        return true;
      }
      return false;
    },
    snapshotConfig() {
      return getSnapshotConfig(this.$route.params.uni);
    },
    saveSelfieConsentOrgName() {
      return this.snapshotConfig.saveSelfieConsentOrgName || "MyGraduationClip";
    },
    saveSelfieConsentPolicyUrl() {
      return this.snapshotConfig.saveSelfieConsentPolicyUrl
        || this.snapshotConfig.consentUrl
        || "/privacy-policy";
    },
    saveSelfieConsentContactEmail() {
      return this.snapshotConfig.consentEmail || "";
    },
    saveSelfieConsentVersion() {
      return this.snapshotConfig.saveSelfieConsentVersion || "v1";
    },
    newImagesTtlDays() {
      return this.snapshotConfig.newImagesTtlDays || 7;
    },
    rememberTtlDays() {
      return this.snapshotConfig.rememberMeTtlDays || 30;
    },
    hasRememberedDetails() {
      if (!this.snapshotConfig.rememberMeEnabled) return false;
      const details = this.personalDetails?.event
        ? this.getRememberedDetails(this.personalDetails.event)
        : null;
      return Boolean(details);
    },
    // Show back only when we’ve actually moved forward inside the flow
    isGoBackStatePossible() {
      return (
        this.stepStack.length > 0 &&
        this.state !== "ceremony" &&
        this.state !== "error"
      );
    },
  },
  created: function () {
    this.loadSnapshotConfig();
    if (this.handleUnsubscribeFromQuery()) {
      return;
    }
    this.initialiseFlow();
  },
  methods: {
    loadSnapshotConfig() {
      const slug = this.$route.params.uni;
      if (!slug || !process.env.VUE_APP_SNAPSHOT_API_BASE) return;
      const params = {};
      if (this.$route.query.ceremony) {
        params.ceremony_id = this.$route.query.ceremony;
      }
      axios
        .get(`${process.env.VUE_APP_SNAPSHOT_API_BASE}/config/${slug}`, { params })
        .then((r) => {
          if (r.data && r.data.config && r.data.configured !== false) {
            setRuntimeSnapshotConfig(slug, r.data.config);
            this.syncCurrentStepWithConfig();
          }
        })
        .catch((e) => {
        console.warn("Unable to load snapshot config", e);
        });
    },
    syncCurrentStepWithConfig() {
      if (this.state === "face" && this.requiresConsent && !this.hasAcceptedConsent()) {
        this.pendingState = "face";
        this.updateState("consent", false);
      }
    },
    consentStorageKey() {
      return this.snapshotConfig.consentStorageKey || "consent:snapshot:v1";
    },
    hasAcceptedConsent() {
      if (typeof window !== 'undefined' && window.localStorage) {
        return window.localStorage.getItem(this.consentStorageKey()) === 'true';
      }
      return false;
    },
    markConsentAccepted() {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(this.consentStorageKey(), 'true');
      }
    },
    handleConsentAccepted() {
      this.markConsentAccepted();
      // Continue to the intended state if set, otherwise resume normal flow
      const next = this.pendingState;
      this.pendingState = null;
      const pendingAutoSearch = this.pendingAutoSearch;
      this.pendingAutoSearch = null;
      try {
        trackEvent(this.$route.path, {
          label: "SNAPSHOT_CONSENT_ACCEPTED",
          university: this.$route.params.uni,
        });
      } catch (_) {
        // Ignore analytics failures.
      }
      if (pendingAutoSearch) {
        this.autoSearchMode = true;
        this.latestSelfieDataUrl = pendingAutoSearch.image;
        this.searchFace(pendingAutoSearch.image, { nextState: "selector" });
        return;
      }
      if (next) {
        this.updateState(next, true);
      } else {
        this.initialiseFlow();
      }
    },
    gotoFaceStep(pushHistory = true) {
      if (this.requiresConsent && !this.hasAcceptedConsent()) {
        // Defer entering the selfie step until consent is accepted
        this.pendingState = "face";
        this.updateState("consent", pushHistory);
        try {
          trackEvent(this.$route.path, {
            label: "SNAPSHOT_CONSENT_SHOWN",
            reason: "BEFORE_SELFIE",
            university: this.$route.params.uni,
          });
        } catch (_) {
          // Ignore analytics failures.
        }
        return;
      }
      try {
        trackEvent(this.$route.path, {
          label: "SNAPSHOT_CONSENT_ALREADY_ACCEPTED",
          step: "FACE",
          university: this.$route.params.uni,
        });
      } catch (_) {
        // Ignore analytics failures.
      }
      this.updateState("face", pushHistory);
    },
    gotoPhotoDiscoveryStep(pushHistory = true) {
      if (this.snapshotConfig.faceSearchRecommended === false) {
        this.selfieSkipped = true;
        this.updateState("details", pushHistory);
        return;
      }
      this.gotoFaceStep(pushHistory);
    },
    initialiseFlow() {
      const share = this.$route.query.shareBasket;
      if (share) {
        this.receiveSharedBasket();
        // Initialisation → don’t push history yet
        this.updateState("selector", false);
        return;
      }
      var sub = this.$route.query.registration;
      if (typeof sub == "undefined") {
        // Initialisation → don’t push history yet
        this.updateState("ceremony", false);
        return;
      }
      axios
        .get(`/registrations/${this.$route.query.registration}`)
        .then((r) => {
          this.registrationID = this.$route.query.registration;
          this.personalDetails.name = r.data.name;
          this.personalDetails.email = r.data.email;
          this.personalDetails.event = r.data.ceremony;
          this.resetFreeDownloadState(true);
          this.populated = true;

          // Initialisation → don’t push history yet
          this.updateState("selector", false);
        })
        .catch(() => {
          this.updateState("ceremony", false);
        })
        .then(() => {
          if (!this.personalDetails?.event || !this.personalDetails?.name) return;
          return axios
            .post(`${process.env.VUE_APP_SNAPSHOT_API_BASE}/images`, {
              ceremony: this.personalDetails.event,
              name: this.personalDetails.name,
            })
            .then((r) => {
              this.allImages = r.data;
              this.galleryImages = r.data.map((x) => {
                return { src: x["preview"] };
              });
              if (r.data.length > 1 && r.data[0].score == r.data[1].score) {
                this.updateState("gallery", false);
              } else if (r.data.length == 1 || r.data[0].score > 0.75) {
                this.gallerySelectionSubmitted(0);
              } else {
                this.updateState("gallery", false);
              }
            })
            .catch(() => {
              this.updateState("error", false);
              trackEvent(this.$route.path, {
                label: "SNAPSHOT_NOT_READY",
              });
            });
        });
    },
    applyCeremonyIdFromQuery() {
      if (this.$route.query.shareBasket || this.$route.query.registration) return;
      if (this.personalDetails?.event) return;
      const ceremonyId = this.$route.query.ceremony;
      if (!ceremonyId || !this.events || this.events.length === 0) return;
      const id = Array.isArray(ceremonyId) ? ceremonyId[0] : ceremonyId;
      if (!this.events.find((c) => c.id === id)) return;
      this.personalDetails.event = id;
      this.resetFreeDownloadState(true);
      this.gotoPhotoDiscoveryStep(false);
    },
    onPopState(e) {
      // Sync our local state when the user hits the browser back button
      if (this.stepStack.length) this.stepStack.pop();
      const step = e.state && e.state.step;
      const allowed = ["ceremony", "gallery", "face", "details", "selector", "consent", "error", "unsubscribe"];
      if (allowed.includes(step)) this.state = step;
    },
    handleUnsubscribeFromQuery() {
      const { unsubscribe, subscription_id: subscriptionId, token } = this.$route.query;
      if (!unsubscribe || !subscriptionId || !token) return false;
      this.state = "unsubscribe";
      this.unsubscribeStatus = "loading";
      axios
        .post(`${process.env.VUE_APP_SNAPSHOT_API_BASE}/subscriptions/unsubscribe`, {
          subscription_id: subscriptionId,
          token,
        })
        .then(() => {
          this.unsubscribeStatus = "success";
        })
        .catch(() => {
          this.unsubscribeStatus = "error";
        });
      return true;
    },
    resetFreeDownloadState(resetFreeFlag = false) {
      if (resetFreeFlag) {
        this.orderFreeDigitalOverride = false;
      }
      this.freeDownloadSuccess = false;
      this.freeDownloadError = null;
      this.freeDownloadInProgress = false;
      this.lastOrderNanoId = "";
    },
    firstname(name) {
      return name.split(" ")[0];
    },
    resolveExcludeLandscape() {
      var printOnly = false;
      if (typeof this.$route.query.printOnly === undefined) {
        printOnly = false;
      } else {
        if (this.$route.query.printOnly == "true") {
          printOnly = true;
        } else {
          printOnly = false;
        }
      }
      return printOnly;
    },
    performFaceSearch(payload, options = {}) {
      // Reset any prior results/errors and display a clear loading state
      this.searchError = null;
      this.searchNoResults = false;
      this.noResultsFound = false;
      this.noResultsSubmitted = false;
      this.noResultsNotifyOptIn = false;
      this.searchInProgress = true;
      this.selfieSkipped = false;
      this.images = [];
      axios
        .post(`${process.env.VUE_APP_SNAPSHOT_API_BASE}/search`, payload)
        .then((r) => {
          this.searchInProgress = false;
          this.autoSearchMode = false;
          const payload = r.data;
          const imageItems = Array.isArray(payload?.image_items)
            ? payload.image_items
            : Array.isArray(payload?.imageItems)
              ? payload.imageItems
              : null;
          const results = imageItems || (Array.isArray(payload)
            ? payload
            : (payload && payload.images) || []);
          if (!results.length) {
            this.noResultsFound = true;
            this.foundCount = 0;
            this.images = [];
            this.updateState("details", true);
            return;
          }
          this.images = this.buildImageResults(results);
          this.foundCount = results.length;
          this.noResultsFound = false;
          const nextState = options.nextState || "details";
          const pushHistory = options.pushHistory !== false;
          this.updateState(nextState, pushHistory); // user action → push history
        })
        .catch(() => {
          // Keep user on face step, clear any stale selector images, and show error
          this.searchInProgress = false;
          this.autoSearchMode = false;
          this.images = [];
          this.searchError = "We had trouble searching for your photos. Please try again or skip to browse.";
        });
    },
    searchFace(dataUrl, options = {}) {
      if (!dataUrl) return;
      this.latestSelfieDataUrl = dataUrl;
      this.performFaceSearch({
        ceremony_id: this.personalDetails.event,
        image: dataUrl,
        exclude_landscape: this.resolveExcludeLandscape(),
      }, options);
    },
    submitCeremony(ceremonyId) {
      this.personalDetails.event = ceremonyId;
      this.resetFreeDownloadState(true);
      this.images = [];
      this.foundCount = 0;
      this.noResultsFound = false;
      this.noResultsSubmitted = false;
      this.noResultsNotifyOptIn = false;
      this.searchError = null;
      this.searchNoResults = false;
      this.selfieSkipped = false;
      this.latestSelfieDataUrl = null;
      trackEvent(this.$route.path, {
        label: "SNAPSHOT_CEREMONY_SELECTED",
        ceremony: ceremonyId,
      });
      this.gotoPhotoDiscoveryStep(true);
    },
    handleSkipSelfie() {
      this.selfieSkipped = true;
      this.foundCount = 0;
      this.noResultsFound = false;
      this.noResultsSubmitted = false;
      this.noResultsNotifyOptIn = false;
      this.latestSelfieDataUrl = null;
      this.updateState("details", true);
    },
    async useRememberedDetails(ceremonyId) {
      if (!this.snapshotConfig.rememberMeEnabled) {
        this.submitCeremony(ceremonyId);
        return;
      }
      const remembered = this.getRememberedDetails(ceremonyId);
      const rememberedSelfie = this.getRememberedSelfie(ceremonyId);
      this.personalDetails.event = ceremonyId;
      if (remembered) {
        this.personalDetails.name = remembered.name;
        this.personalDetails.email = remembered.email;
      }
      this.resetFreeDownloadState(true);
      this.images = [];
      this.foundCount = 0;
      this.noResultsFound = false;
      this.noResultsSubmitted = false;
      this.noResultsNotifyOptIn = false;
      this.searchError = null;
      this.searchNoResults = false;
      this.selfieSkipped = false;
      this.latestSelfieDataUrl = null;
      if (remembered && rememberedSelfie) {
        this.latestSelfieDataUrl = rememberedSelfie;
        if (this.requiresConsent && !this.hasAcceptedConsent()) {
          this.pendingAutoSearch = {
            ceremony_id: ceremonyId,
            image: rememberedSelfie,
          };
          this.pendingState = null;
          this.updateState("consent", true);
          try {
            trackEvent(this.$route.path, {
              label: "SNAPSHOT_CONSENT_SHOWN",
              reason: "BEFORE_AUTO_SEARCH",
              university: this.$route.params.uni,
            });
          } catch (_) {
            // Ignore analytics failures.
          }
          return;
        }
        this.autoSearchMode = true;
        this.updateState("face", true);
        this.searchFace(rememberedSelfie, { nextState: "selector" });
        return;
      }
      this.gotoPhotoDiscoveryStep(true);
    },
    async submitContactDetails(formData) {
      const previousEvent = this.personalDetails?.event;
      this.personalDetails.name = formData.name;
      this.personalDetails.email = formData.email;
      const ceremonyChanged = previousEvent !== this.personalDetails.event;
      this.resetFreeDownloadState(ceremonyChanged);
      trackEvent(this.$route.path, {
        label: "SNAPSHOT_CONTACT_DETAILS_SUBMITTED",
        ceremony: this.personalDetails.event,
      });
      const rememberEnabled = this.snapshotConfig.rememberMeEnabled
        && Boolean(formData.rememberMe);
      if (rememberEnabled) {
        const savedAt = Date.now();
        this.storeRememberedDetails(
          this.personalDetails.event,
          this.personalDetails.name,
          this.personalDetails.email,
          savedAt
        );
        if (this.latestSelfieDataUrl) {
          this.storeRememberedSelfie(
            this.personalDetails.event,
            this.latestSelfieDataUrl,
            savedAt
          );
        }
      }
      const shouldSaveSelfie = this.snapshotConfig.saveSelfieEnabled
        && Boolean(formData.saveSelfie)
        && Boolean(this.latestSelfieDataUrl);
      const canSubscribe = shouldSaveSelfie && (!this.requiresConsent || this.hasAcceptedConsent());
      if (canSubscribe) {
        try {
          await this.subscribeForSelfieMatches();
        } catch (err) {
          console.warn("Failed to save selfie subscription", err);
        }
      }
      if (this.noResultsFound) {
        this.noResultsNotifyOptIn = Boolean(canSubscribe);
        this.noResultsSubmitted = true;
        return;
      }
      if (!this.selfieSkipped) {
        this.updateState("selector", true);
        return;
      }
      this.fetchImagesForName();
    },
    fetchImagesForName() {
      axios
        .post(`${process.env.VUE_APP_SNAPSHOT_API_BASE}/images`, {
          ceremony: this.personalDetails.event,
          name: this.personalDetails.name,
          email: this.personalDetails.email,
        })
        .then((r) => {
          this.allImages = r.data;
          this.galleryImages = r.data.map((x) => {
            return { src: x["preview"] };
          });
          if (r.data.length > 1 && r.data[0].score == r.data[1].score) {
            this.updateState("gallery", true);
          } else if (r.data.length == 1 || r.data[0].score > 0.75) {
            this.gallerySelectionSubmitted(0);
          } else {
            this.updateState("gallery", true);
          }
          this.noResultsFound = false;
          this.noResultsSubmitted = false;
          this.noResultsNotifyOptIn = false;

          const [minScoreVal, maxScoreVal] = this.calculateScoreRanges(r.data);
          let minScore = minScoreVal;
          let maxScore = maxScoreVal;
          const [minImagesVal, maxImagesVal, totalImages] =
            this.calculateImageRanges(r.data);
          let minImages = minImagesVal;
          let maxImages = maxImagesVal;
          const meanImages =
            r.data.length > 0 ? totalImages / r.data.length : 0;
          minImages = minImages === Infinity ? 0 : minImages;

          trackEvent(this.$route.path, {
            label: "SNAPSHOT_IMAGES_FROM_TRANSCRIPT_RESPONSE",
            API_endpoint: "/images",
            image_groups_found: r.data.length,
            score_ranges: {
              min_score: minScore,
              max_score: maxScore,
            },
            image_ranges: {
              min_images: minImages,
              max_images: maxImages,
              mean_images: meanImages.toFixed(2),
            },
          });
        })
        .catch(() => {
          this.updateState("error", true);
        });
    },
    rememberStorageKey(ceremonyId) {
      const slug = this.$route.params.uni || "snapshot";
      return `snapshot:remember:${slug}:${ceremonyId}`;
    },
    rememberSelfieStorageKey(ceremonyId) {
      const slug = this.$route.params.uni || "snapshot";
      return `snapshot:selfie:${slug}:${ceremonyId}`;
    },
    lastSearchStorageKey(ceremonyId) {
      const slug = this.$route.params.uni || "snapshot";
      return `snapshot:last-search:${slug}:${ceremonyId}`;
    },
    normalizeImageKey(src) {
      if (!src) return "";
      const str = String(src);
      const marker = "/thumbnails/";
      let key = str.includes(marker) ? (str.split(marker)[1] || "") : (str.split("/").pop() || "");
      key = key.split("?")[0];
      return key;
    },
    loadLastSearchKeys(ceremonyId) {
      if (!ceremonyId) return null;
      try {
        if (typeof window === "undefined" || !window.localStorage) return null;
        const raw = localStorage.getItem(this.lastSearchStorageKey(ceremonyId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || !Array.isArray(parsed.keys) || !parsed.savedAt) return null;
        const ageMs = Date.now() - Number(parsed.savedAt);
        const maxAgeMs = this.newImagesTtlDays * 24 * 60 * 60 * 1000;
        if (ageMs > maxAgeMs) return null;
        return parsed.keys.filter(Boolean);
      } catch (e) {
        return null;
      }
    },
    saveLastSearchKeys(ceremonyId, keys) {
      if (!ceremonyId) return;
      try {
        if (typeof window === "undefined" || !window.localStorage) return;
        const uniqueKeys = Array.from(new Set((keys || []).filter(Boolean)));
        localStorage.setItem(
          this.lastSearchStorageKey(ceremonyId),
          JSON.stringify({ savedAt: Date.now(), keys: uniqueKeys })
        );
      } catch (e) {
        // Ignore storage failures
      }
    },
    buildImageResults(results) {
      const ceremonyId = this.personalDetails?.event;
      const previousKeys = this.loadLastSearchKeys(ceremonyId);
      const previousSet = previousKeys ? new Set(previousKeys) : null;
      const images = (results || []).map((item) => {
        const source = typeof item === "string" ? { src: item } : { ...(item || {}) };
        const src = source.src || source.url || "";
        const key = this.normalizeImageKey(src);
        const isNew = previousSet ? !previousSet.has(key) : false;
        return { ...source, src, isNew };
      });
      let ordered = images;
      if (previousSet) {
        const newer = images.filter((img) => img.isNew);
        const existing = images.filter((img) => !img.isNew);
        ordered = [...newer, ...existing];
      }
      const currentKeys = ordered.map((img) => this.normalizeImageKey(img.src));
      this.saveLastSearchKeys(ceremonyId, currentKeys);
      return ordered;
    },
    clearRememberedDetails(ceremonyId) {
      if (!ceremonyId) return;
      try {
        localStorage.removeItem(this.rememberStorageKey(ceremonyId));
        localStorage.removeItem(this.rememberSelfieStorageKey(ceremonyId));
      } catch (e) { /* noop */ }
    },
    getRememberedDetails(ceremonyId) {
      if (!ceremonyId) return null;
      try {
        const raw = localStorage.getItem(this.rememberStorageKey(ceremonyId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.name || !parsed.email || !parsed.savedAt) return null;
        const ageMs = Date.now() - Number(parsed.savedAt);
        const maxAgeMs = this.rememberTtlDays * 24 * 60 * 60 * 1000;
        if (ageMs > maxAgeMs) {
          this.clearRememberedDetails(ceremonyId);
          return null;
        }
        return { name: parsed.name, email: parsed.email };
      } catch (e) {
        return null;
      }
    },
    storeRememberedDetails(ceremonyId, name, email, savedAt = Date.now()) {
      if (!ceremonyId || !name || !email) return;
      try {
        localStorage.setItem(
          this.rememberStorageKey(ceremonyId),
          JSON.stringify({ name, email, savedAt })
        );
      } catch (e) { /* noop */ }
    },
    getRememberedSelfie(ceremonyId) {
      if (!ceremonyId) return null;
      try {
        if (typeof window === "undefined" || !window.localStorage) return null;
        const raw = localStorage.getItem(this.rememberSelfieStorageKey(ceremonyId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.dataUrl || !parsed.savedAt) return null;
        const ageMs = Date.now() - Number(parsed.savedAt);
        const maxAgeMs = this.rememberTtlDays * 24 * 60 * 60 * 1000;
        if (ageMs > maxAgeMs) {
          localStorage.removeItem(this.rememberSelfieStorageKey(ceremonyId));
          return null;
        }
        return parsed.dataUrl;
      } catch (e) {
        return null;
      }
    },
    storeRememberedSelfie(ceremonyId, dataUrl, savedAt = Date.now()) {
      if (!ceremonyId || !dataUrl) return;
      try {
        if (typeof window === "undefined" || !window.localStorage) return;
        localStorage.setItem(
          this.rememberSelfieStorageKey(ceremonyId),
          JSON.stringify({ dataUrl, savedAt })
        );
      } catch (e) { /* noop */ }
    },
    forgetRememberedDetails() {
      const ceremonyId = this.personalDetails?.event;
      if (!ceremonyId) return;
      this.clearRememberedDetails(ceremonyId);
    },
    retakeSelfieFromDetails() {
      this.searchError = null;
      this.searchNoResults = false;
      this.noResultsFound = false;
      this.noResultsSubmitted = false;
      this.noResultsNotifyOptIn = false;
      this.images = [];
      this.foundCount = 0;
      this.latestSelfieDataUrl = null;
      this.gotoFaceStep(true);
    },
    async subscribeForSelfieMatches() {
      const body = {
        ceremony_id: this.personalDetails.event,
        email: this.personalDetails.email,
        name: this.personalDetails.name,
        image: this.latestSelfieDataUrl,
        consent: true,
        consent_version: this.saveSelfieConsentVersion,
      };
      return axios.post(`${process.env.VUE_APP_SNAPSHOT_API_BASE}/subscriptions`, body);
    },
    calculateScoreRanges(data) {
      // Calculate min and max scores of groups returned
      // From /images request
      let minScore = 0;
      let maxScore = 0;
      if (data.length > 0) {
        let scores = data
          .map((item) => item.score)
          .filter((score) => score !== undefined);
        minScore = Math.min(...scores);
        maxScore = Math.max(...scores);
      }
      return [minScore, maxScore];
    },
    calculateImageRanges(data) {
      // Calculate ranges of images per group returned
      // From /images request
      let totalImages = 0;
      let minImages = Infinity;
      let maxImages = 0;

      data.forEach((item) => {
        if (item.images && Array.isArray(item.images)) {
          const imageCount = item.images.length;
          totalImages += imageCount;
          minImages = Math.min(minImages, imageCount);
          maxImages = Math.max(maxImages, imageCount);
        }
      });
      return [minImages, maxImages, totalImages];
    },
    notMeClick() {
      trackEvent(this.$route.path, {
        label: "SNAPSHOT_NOT_ME",
      });
      var newImages = [];
      this.allImages.forEach((element) => {
        if (element.previous != null) {
          newImages.push(element.previous);
        }
        if (element.next != null) {
          newImages.push(element.next);
        }
      });
      this.allImages = newImages;
      this.showingPreviousNext = true;
      this.galleryImages = newImages.map((x) => {
        return { src: x["preview"] };
      });
    },
    submitImageSelection(items, form) {
      if (form) {
        this.personalDetails["name"] = form["name"];
        this.personalDetails["email"] = form["email"];
      }
      this.resetFreeDownloadState();
      axios
        .post(`${process.env.VUE_APP_SNAPSHOT_API_BASE}/orders`, {
          name: this.personalDetails["name"],
          email: this.personalDetails["email"],
          ceremony: this.personalDetails["event"],
          slug: this.$route.params.uni,
          tenant: "mygraduationfilm",
          items: items,
        })
        .then((r) => {
          trackEvent(this.$route.path, {
            label: "SNAPSHOT_SUBMIT_ORDER_RESPONSE",
          });
          const data = r.data;
          if (typeof data === "string") {
            window.location.href = data;
            return;
          }
          if (
            data &&
            Object.prototype.hasOwnProperty.call(data, "digitalFreeCeremony")
          ) {
            this.orderFreeDigitalOverride = Boolean(data.digitalFreeCeremony);
          }
          const redirectUrl = data?.redirectUrl;
          const freeOrder = data?.freeOrder;
          const orderNanoId = data?.orderNanoId;
          if (freeOrder) {
            trackEvent(this.$route.path, {
              label: "SNAPSHOT_FREE_ORDER_CREATED",
              orderNanoId: orderNanoId,
            });
            this.lastOrderNanoId = orderNanoId || "";
            this.triggerFreeDownload(orderNanoId);
            return;
          }
          if (redirectUrl) {
            window.location.href = redirectUrl;
            return;
          }
          if (data?.url) {
            window.location.href = data.url;
            return;
          }
          console.warn("Unexpected order response", data);
        })
        .catch((error) => {
          console.error(error);
          alert(
            "Something went wrong while submitting your order. Please try again."
          );
        });
    },
    gallerySelectionSubmitted(index) {
      trackEvent(this.$route.path, {
        label: "SNAPSHOT_GALLERY_SELECTION_SUBMITTED",
        img_index: index,
        img_src: this.allImages[index]["preview"],
      });
      // Fix this
      this.images = this.buildImageResults(this.allImages[index]["images"]);

      // if(this.displayProductType(this.events)){
      //   this.updateState("type", true);
      // } else {
      //   this.updateState("selector", true);
      // }
      // No URL change — keep internal state only
      this.updateState("selector", true);
    },
    updateState(newState, pushHistory = true) {
      if (pushHistory && window?.history?.pushState) {
        this.stepStack.push(this.state);
        // Push a history entry without changing the URL
        window.history.pushState({ step: newState }, "", window.location.href);
      }
      this.state = newState;
    },
    addStatetoQuery(newState) {
      const currentQuery = { ...this.$route.query };
      currentQuery.state = newState;
      // Force digital-only everywhere
      delete currentQuery.printOnly;
      currentQuery.digitalOnly = "true";
      this.$router
        .push({
          path: this.$route.path,
          query: currentQuery,
        })
        .catch((err) => {
          if (err.name !== "NavigationDuplicated") {
            throw err;
          }
        });
    },
    goBackState() {
      if (this.stepStack.length) {
        window.history.back(); // popstate listener will update `state` and stack
      }
    },
    shareBasket(items) {
      const imageEncode = this.encodeImagesForSharing(items);
      const origin = window.location.origin;
      const url = `${origin}/snapshot/${this.$route.params.uni}?shareBasket=true&ceremony=${this.personalDetails["event"]}&items=${imageEncode}&digitalOnly=true`;

      this.shareBasketdata = url;
    },
    retryFreeDownload() {
      if (!this.lastOrderNanoId) {
        return;
      }
      this.triggerFreeDownload(this.lastOrderNanoId);
    },
    triggerFreeDownload(orderNanoId) {
      if (!orderNanoId) {
        this.freeDownloadError = "We couldn't find your order reference.";
        return;
      }
      this.freeDownloadSuccess = false;
      this.freeDownloadInProgress = true;
      this.freeDownloadError = null;
      trackEvent(this.$route.path, {
        label: "SNAPSHOT_FREE_ORDER_DOWNLOAD_START",
        orderNanoId: orderNanoId,
      });
      axios
        .post(`${process.env.VUE_APP_SNAPSHOT_API_BASE}/download-digitals`, {
          order_nano_id: orderNanoId,
          email: this.personalDetails.email,
        })
        .then((response) => {
          const downloadUrl = response?.data?.url;
          if (downloadUrl) {
            const newWindow = window.open(downloadUrl, "_blank");
            if (!newWindow) {
              window.location.href = downloadUrl;
            }
            this.freeDownloadSuccess = true;
            trackEvent(this.$route.path, {
              label: "SNAPSHOT_FREE_ORDER_DOWNLOAD_SUCCESS",
              orderNanoId: orderNanoId,
            });
            if (this.$refs?.imageSelector?.resetSelection) {
              this.$refs.imageSelector.resetSelection();
            }
            return;
          }
          this.freeDownloadError =
            "We couldn't generate your download link. Please try again.";
          trackEvent(this.$route.path, {
            label: "SNAPSHOT_FREE_ORDER_DOWNLOAD_FAILED",
            orderNanoId: orderNanoId,
            message: this.freeDownloadError,
          });
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message ||
            "We couldn't prepare your download right now. Please try again.";
          this.freeDownloadError = message;
          trackEvent(this.$route.path, {
            label: "SNAPSHOT_FREE_ORDER_DOWNLOAD_FAILED",
            orderNanoId: orderNanoId,
            message: message,
          });
        })
        .finally(() => {
          this.freeDownloadInProgress = false;
        });
    },
    encodeImagesForSharing(items) {
      const encodedImages = items.map((item) => {
        const src = item && item.src ? String(item.src) : "";
        const marker = "/thumbnails/";
        let key = "";
        if (src.includes(marker)) {
          key = src.split(marker)[1] || "";
        } else {
          key = src.split("/").pop() || "";
        }
        key = key.split("?")[0];
        return btoa(key);
      });
      return encodeURIComponent(JSON.stringify(encodedImages));
    },
    receiveSharedBasket() {
      // Decode items
      let uriDecode = decodeURIComponent(this.$route.query.items);
      let itemsJson = JSON.parse(uriDecode);
      //const isPrintPresent = this.$route.query.printOnly ? true : false;
      //const isPrintPresent = false;
      const event = this.$route.query?.ceremony;
      // Create item objs
      let itemsRetrieved = itemsJson.map((item) => {
        const decodedSrc = `${
          process.env.VUE_APP_SNAPSHOT_CF_DISTRIBUTION_URL
        }/${event}/thumbnails/${atob(item)}`;
        return {
          src: decodedSrc,
          isDigitalSelected: true,
          isPrintSelected: false,
          printQuantity: 0,
        };
      });
      this.images = itemsRetrieved;
      const previousEvent = this.personalDetails?.event;
      this.personalDetails["event"] = event;
      this.resetFreeDownloadState(previousEvent !== event);
    },
    submitProductType(type) {
      this.updateState("selector", true);
      const queryParams = { ...this.$route.query };
      switch (type) {
        case "print":
          delete queryParams.digitalOnly;
          queryParams.printOnly = "true";
          this.$router.push({ query: queryParams });
          break;
        case "digital":
          delete queryParams.printOnly;
          queryParams.digitalOnly = "true";
          this.$router.push({ query: queryParams });
          break;
      }
    },
    // displayProductType(events){
    //   // Do we need to display the product type select options
    //   const printing = this.$route.query.printOnly === 'true' ? true : false;
    //   const digitally = this.$route.query.digitalOnly === 'true' ? true : false;
    //   if(printing || digitally){
    //     // Method already set
    //     return false;
    //   }
    //   // Remove hardcoded date that was causing issues
    //   // events[0]['datetime'] = '2025-01-24 13:30:00';
    //   const today = new Date();
    //   today.setHours(0, 0, 0, 0);
    //   // TODO: Factor for timezones
    //   const totalToday = events.filter(e => {
    //     const eventDate = new Date(e.datetime);
    //     eventDate.setHours(0, 0, 0, 0);
    //     return eventDate.getTime() === today.getTime();
    //   });
    //   if(totalToday.length > 0){
    //     // Events on today
    //     return false;
    //   }
    //   // No events today
    //   return true;
    // },
    displayProductType() {
      // Hard-disable product type and force digitalOnly in URL
      const query = { ...this.$route.query };
      delete query.printOnly;
      if (query.digitalOnly !== "true") {
        query.digitalOnly = "true";
        this.$router.replace({ query }).catch((err) => {
          if (err.name !== "NavigationDuplicated") {
            throw err;
          }
        });
      }
      return false;
    },
  },
};
</script>
  
